'use client'

import { type ReactNode } from 'react'
import Link from 'next/link'
import { type FulfillmentCity } from '@entities/fulfillment'
import { Badge } from '@ui/base/badge'
import { Button } from '@ui/base/button'

import { type CatalogSearchParams } from '../lib/search-params'
import { useCatalogFilters } from './catalog-filters-context'

/** Города — это список, а не набор кнопок: выбранный подсвечен фоном, остальные спокойные. */
const CityOption = ({ cityId, active, children }: { cityId?: string; active: boolean; children: ReactNode }) => {
  const { linkProps } = useCatalogFilters()

  return (
    <Button
      size="sm"
      variant={active ? 'secondary' : 'ghost'}
      className={`justify-between ${active ? 'font-medium' : 'text-muted-foreground'}`}
      render={
        <Link {...linkProps(() => ({ city: cityId, page: undefined }))} aria-current={active ? 'page' : undefined} />
      }
    >
      {children}
    </Button>
  )
}

export const CityAllLink = ({ active }: { active: boolean }) => <CityOption active={active}>Все города РФ</CityOption>

export const CityLink = ({ city, active }: { city: FulfillmentCity; active: boolean }) => (
  <CityOption cityId={city.id} active={active}>
    {city.name}
    {city.fulfillmentCount !== null && <Badge variant="secondary">{city.fulfillmentCount}</Badge>}
  </CityOption>
)

const sortOptions = [
  ['rating', 'Сначала лучшие'],
  ['newest', 'Сначала новые'],
] as const

/**
 * «Сначала лучшие» — это не голая средняя оценка: карточка с двумя пятёрками не должна
 * обгонять карточку с сотней отзывов, поэтому бэкенд сглаживает оценку по числу отзывов.
 */
export const SortLinks = ({ params }: { params: CatalogSearchParams }) => {
  const { linkProps } = useCatalogFilters()

  return (
    <div role="group" aria-label="Порядок списка" className="bg-muted flex flex-wrap gap-0.5 rounded-md p-0.5">
      {sortOptions.map(([value, label]) => {
        const active = params.sort === value
        return (
          <Button
            key={value}
            size="sm"
            variant="ghost"
            className={active ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}
            render={
              <Link
                {...linkProps(() => ({ sort: value, page: undefined }))}
                aria-current={active ? 'true' : undefined}
              />
            }
          >
            {label}
          </Button>
        )
      })}
    </div>
  )
}
